import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  LinearProgress,
  Alert,
  Autocomplete,
  TextField,
  Grid
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { getItemCodes, getBrands } from '../api/items';

// Options for image type dropdown
const TYPE_OPTIONS = ['front', 'back', 'side', 'external', 'standard'];

export default function ImageUpload({ onUploadSuccess, onUploadError, fixedMetadata = null, hideFixedFields = false }) {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  
  // Options loaded from API
  const [brandOptions, setBrandOptions] = useState([]);
  const [itemCodeOptions, setItemCodeOptions] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  
  // Metadata fields - use fixed values if provided
  const [brand, setBrand] = useState(fixedMetadata?.brand || null);
  const [itemCode, setItemCode] = useState(fixedMetadata?.itemCode || null);
  const [type, setType] = useState(fixedMetadata?.type || 'front');
  const [sortOrder, setSortOrder] = useState(1);
  
  const isMetadataFixed = fixedMetadata !== null;

  // Re-sync state when fixedMetadata changes (e.g. modal opened for a different item)
  useEffect(() => {
    if (fixedMetadata) {
      setBrand(fixedMetadata.brand || null);
      setItemCode(fixedMetadata.itemCode || null);
      setType(fixedMetadata.type || 'front');
    }
  }, [fixedMetadata?.brand, fixedMetadata?.itemCode, fixedMetadata?.type]);

  // Load brands and item codes on mount
  useEffect(() => {
    loadBrands();
    loadItemCodes();
  }, []);

  const loadBrands = async () => {
    try {
      setLoadingBrands(true);
      const result = await getBrands();
      if (result.success) {
        setBrandOptions(result.data || []);
      }
    } catch (err) {
      console.error('Failed to load brands:', err);
    } finally {
      setLoadingBrands(false);
    }
  };

  const loadItemCodes = async (searchTerm = '') => {
    try {
      setLoadingItems(true);
      const result = await getItemCodes(searchTerm, 100);
      if (result.success) {
        setItemCodeOptions(result.data || []);
      }
    } catch (err) {
      console.error('Failed to load item codes:', err);
    } finally {
      setLoadingItems(false);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFile = (file) => {
    setError(null);

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload a JPEG, PNG, or WebP image.');
      return;
    }

    // Validate file size (10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('File size exceeds 10MB. Please upload a smaller image.');
      return;
    }

    setSelectedFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleClear = () => {
    setSelectedFile(null);
    setPreview(null);
    setUploadProgress(0);
    setError(null);
    // Restore fixed values if provided, otherwise clear
    setBrand(fixedMetadata?.brand || null);
    setItemCode(fixedMetadata?.itemCode || null);
    setType(fixedMetadata?.type || 'front');
    setSortOrder(1);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    // Validate metadata
    if (!brand || !itemCode || !type) {
      setError('Please select Brand, Item Code, and Type before uploading');
      return;
    }

    // Validate sort order
    if (!sortOrder || sortOrder < 1) {
      setError('Please enter a valid Sort Order (must be 1 or greater)');
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // Call the parent component's upload handler with metadata including sortOrder
      await onUploadSuccess(selectedFile, { brand, itemCode, type, sortOrder }, (progress) => {
        setUploadProgress(progress);
      });
    } catch (err) {
      setError(err.message || 'Upload failed');
      if (onUploadError) {
        onUploadError(err);
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Upload Product Photo
      </Typography>

      {/* Metadata Selection Fields */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Only show Brand field if not hidden */}
        {!(hideFixedFields && isMetadataFixed) && (
          <Grid item xs={12} md={4}>
            <Autocomplete
              value={brand}
              onChange={(event, newValue) => {
                if (typeof newValue === 'string') {
                  setBrand(newValue);
                } else if (newValue && newValue.brand) {
                  setBrand(newValue.brand);
                } else {
                  setBrand(null);
                }
              }}
              options={brandOptions}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.brand || '';
              }}
              isOptionEqualToValue={(option, value) => {
                if (typeof option === 'string' && typeof value === 'string') {
                  return option === value;
                }
                return option.brand === value;
              }}
              loading={loadingBrands}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Brand"
                  required
                  placeholder="Select brand"
                />
              )}
              disabled={isUploading || isMetadataFixed}
              readOnly={isMetadataFixed}
            />
          </Grid>
        )}
        {/* Only show Item Code field if not hidden */}
        {!(hideFixedFields && isMetadataFixed) && (
          <Grid item xs={12} md={4}>
          <Autocomplete
            value={itemCode}
            onChange={(event, newValue) => {
              if (typeof newValue === 'string') {
                setItemCode(newValue);
              } else if (newValue && newValue.item_code) {
                setItemCode(newValue.item_code);
                // Auto-select brand when item is selected
                if (newValue.brand && !isMetadataFixed) {
                  setBrand(newValue.brand);
                }
              } else {
                setItemCode(null);
              }
            }}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'input' && newInputValue) {
                loadItemCodes(newInputValue);
              }
            }}
            options={itemCodeOptions}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return option.item_code || '';
            }}
            renderOption={(props, option) => (
              <li {...props}>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {option.item_code}
                  </Typography>
                  {option.item_name && (
                    <Typography variant="caption" color="text.secondary">
                      {option.item_name} {option.brand && `- ${option.brand}`}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            isOptionEqualToValue={(option, value) => {
              if (typeof option === 'string' && typeof value === 'string') {
                return option === value;
              }
              return option.item_code === value;
            }}
            loading={loadingItems}
            filterOptions={(x) => x}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Item Code"
                required
                placeholder="Type to search..."
              />
            )}
            disabled={isUploading || isMetadataFixed}
            readOnly={isMetadataFixed}
          />
        </Grid>
        )}
        <Grid item xs={12} md={hideFixedFields && isMetadataFixed ? 6 : 4}>
          <Autocomplete
            value={type}
            onChange={(event, newValue) => setType(newValue)}
            options={TYPE_OPTIONS}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Image Type"
                required
                placeholder="Select type"
              />
            )}
            disabled={isUploading || (isMetadataFixed && fixedMetadata?.type != null)}
          />
        </Grid>
        <Grid item xs={12} md={hideFixedFields && isMetadataFixed ? 6 : 4}>
          <TextField
            label="Sort Order"
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(parseInt(e.target.value) || 1)}
            required
            placeholder="Enter sort order"
            disabled={isUploading}
            inputProps={{ min: 1 }}
            helperText="Order within the same image type (1, 2, 3...)"
            fullWidth
          />
        </Grid>
      </Grid>

      <Box
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          border: '2px dashed',
          borderColor: isDragging ? 'primary.main' : 'grey.300',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          backgroundColor: isDragging ? 'action.hover' : 'background.paper',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover'
          }
        }}
        onClick={!selectedFile ? handleUploadClick : undefined}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/jpg"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {!preview ? (
          <>
            <UploadIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Drag & Drop Image Here
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              or click to browse
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Supported formats: JPEG, PNG, WebP (Max 10MB)
            </Typography>
          </>
        ) : (
          <Box>
            <Box
              component="img"
              src={preview}
              alt="Preview"
              sx={{
                maxWidth: '100%',
                maxHeight: 400,
                objectFit: 'contain',
                borderRadius: 1,
                mb: 2
              }}
            />
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
            </Typography>
          </Box>
        )}
      </Box>

      {isUploading && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Uploading and analyzing... {uploadProgress}%
          </Typography>
          <LinearProgress variant="determinate" value={uploadProgress} />
        </Box>
      )}

      {selectedFile && !isUploading && (
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 2, fontWeight: 500 }} 
              onClose={() => setError(null)}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Upload Failed
              </Typography>
              <Typography variant="body2">
                {error}
              </Typography>
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<ImageIcon />}
              onClick={handleUpload}
              fullWidth
            >
              Analyze Image Quality
            </Button>
            <Button variant="outlined" onClick={handleClear}>
              Clear
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
}
